import React, { useCallback, useMemo, useState } from 'react';
import { Input, Loader, Text } from 'src/@vymo/ui/atoms';
import { Alert } from 'src/@vymo/ui/blocks';
import { ReactComponent as ErrorIcon } from 'src/assets/icons/error.svg';
import { ReactComponent as PendingIcon } from 'src/assets/icons/pending.svg';
import { Keys, locale } from 'src/i18n';
import { useAppSelector } from 'src/store/hooks';
import PDDContent from './pddContent';
import {
  getMyPDDCriticalCount,
  getMyPDDError,
  getMyPDDLoading,
  getMyPDDLoans,
  getMyPDDPendingCount,
} from './selectors';
import SummaryCard from './summaryCard';
import styles from './index.module.scss';

function ABCGetMyPDD() {
  const loans = useAppSelector(getMyPDDLoans);
  const pendingCount = useAppSelector(getMyPDDPendingCount);
  const criticalCount = useAppSelector(getMyPDDCriticalCount);
  const isLoading = useAppSelector(getMyPDDLoading);
  const error = useAppSelector(getMyPDDError);
  const [expandedLoanNo, setExpandedLoanNo] = useState<string>('');
  const [searchText, setSearchText] = useState('');

  const filteredLoans = useMemo(() => {
    const normalizedSearchText = searchText.trim().toLowerCase();

    if (!normalizedSearchText) {
      return loans;
    }

    return loans.filter(
      (loan) =>
        loan?.loanNo?.toLowerCase().includes(normalizedSearchText) ||
        loan?.applicationNumber
          ?.toLowerCase()
          ?.includes(normalizedSearchText) ||
        loan?.customerName?.toLowerCase()?.includes(normalizedSearchText),
    );
  }, [loans, searchText]);

  const handleLoanClick = useCallback((loanNo: string, isOpen: boolean) => {
    setExpandedLoanNo(isOpen ? loanNo : '');
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
    setExpandedLoanNo('');
  }, []);

  return (
    <div className={styles.myPDD}>
      <div className={styles.myPDD__header}>
        <div className={styles.myPDD__titleGroup}>
          <Text type="h5" bold classNames={styles.myPDD__title}>
            {locale(Keys.ABC_PDD_TITLE)}
          </Text>
          <Text classNames={styles.myPDD__subtitle}>
            {locale(Keys.ABC_PDD_SUBTITLE)}
          </Text>
        </div>

        <div className={styles.myPDD__summary}>
          <SummaryCard
            label={locale(Keys.ABC_PDD_PENDING_DOCUMENTS)}
            value={pendingCount}
            variant="pending"
            icon={<PendingIcon />}
          />
          <SummaryCard
            label={locale(Keys.ABC_PDD_CRITICAL_DOCUMENTS)}
            value={criticalCount}
            variant="critical"
            icon={<ErrorIcon />}
          />
        </div>
      </div>

      <div className={styles.myPDD__search}>
        <Input
          value={searchText}
          placeholder={locale(Keys.ABC_PDD_SEARCH_PLACEHOLDER)}
          onChange={handleSearch}
          clearInputIcon
          hideValidation
        />
      </div>

      <Alert
        variant="error"
        banner
        classNames={styles.myPDD__payoutAlert}
        data-test-id="my-pdd-payout-alert"
      >
        {locale(Keys.ABC_PDD_PAYOUT_HOLD_MESSAGE)}
      </Alert>

      <Loader visible={isLoading} className={styles.myPDD__loader}>
        <PDDContent
          error={error}
          loans={loans}
          filteredLoans={filteredLoans}
          isLoading={isLoading}
          expandedLoanNo={expandedLoanNo}
          onLoanClick={handleLoanClick}
        />
      </Loader>
    </div>
  );
}

export default ABCGetMyPDD;
